import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { Badge, Button } from "../../components/ui";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { agentApi, ingestionApi } from "../../api/client";


// ─── SummaryLine ─────────────────────────────────────────────────────────
//
// Renders the structured-aware completion line under each job's progress
// bar. Handles three result_summary shapes (see migration 036):
//
//   {type: "structured", tables: [{name, rows, columns}]}
//   {type: "unstructured", chunks: N}
//   {type: "batch", results: [{filename, pipeline, table, rows, chunks}, ...]}
//
// Falls back to totalChunks for legacy jobs (pre-migration) that have no
// result_summary.
// Exported for unit testing.
export function SummaryLine({ summary, totalChunks }) {
  if (summary?.type === "structured") {
    const t = summary.tables?.[0];
    if (!t) return null;
    const rows = (t.rows || 0).toLocaleString();
    const tbl = t.name || "SQL table";
    return <span>{rows} row{t.rows === 1 ? "" : "s"} → <code style={{ fontFamily: "monospace" }}>{tbl}</code></span>;
  }
  if (summary?.type === "unstructured") {
    const n = summary.chunks || 0;
    if (n === 0) return null;
    return <span>{n} chunk{n === 1 ? "" : "s"}</span>;
  }
  if (summary?.type === "batch") {
    const results = summary.results || [];
    const structuredCount = results.filter(r => r.pipeline === "structured").length;
    const unstructuredCount = results.filter(r => r.pipeline === "unstructured").length;
    const totalRows = results.reduce((sum, r) => sum + (r.rows || 0), 0);
    const totalChunksInBatch = results.reduce((sum, r) => sum + (r.chunks || 0), 0);
    const parts = [];
    if (structuredCount > 0) {
      parts.push(`${structuredCount} table${structuredCount === 1 ? "" : "s"} (${totalRows.toLocaleString()} rows)`);
    }
    if (unstructuredCount > 0) {
      parts.push(`${unstructuredCount} document${unstructuredCount === 1 ? "" : "s"} (${totalChunksInBatch} chunks)`);
    }
    return <span>{parts.join(" · ")}</span>;
  }
  if (totalChunks > 0) {
    return <span>{totalChunks} chunk{totalChunks === 1 ? "" : "s"}</span>;
  }
  return null;
}


// Structured error banner.
//
// `error_message` arrives in two shapes:
//   - Structured (engine path): JSON `{code, message, deep_link?, stage?, technical?}`
//     — emitted by the worker via `IngestionEngineError.to_error_message()`
//     after `user_facing_errors.map_exception()` translates the underlying
//     exception. Renders title + body + optional CTA + collapsible
//     "Technical details" with the raw asyncpg / RuntimeError str.
//   - Plain string (legacy callers, very old jobs, non-JSON crashes):
//     falls back to a single-line banner — same look as before.
//
// The point: the user reads "This agent's KB was indexed at 1536-dim;
// re-embed required" + a Migrate-Embeddings button, not a 600-char
// asyncpg stack. Engineers click "Show technical details" when needed.
// Exported for unit testing.
export function ErrorBanner({ raw }) {
  const [showTech, setShowTech] = useState(false);
  let parsed = null;
  if (typeof raw === "string" && raw.trim().startsWith("{")) {
    try { parsed = JSON.parse(raw); } catch { parsed = null; }
  }

  const isStructured = parsed && typeof parsed === "object" && parsed.message;
  const code = parsed?.code || null;
  const stage = parsed?.stage || null;
  const message = isStructured ? parsed.message : raw;
  const deepLink = parsed?.deep_link || null;
  const technical = parsed?.technical || null;

  // Code-specific accents. Embedding mismatch / migration / not-configured
  // are admin-actionable amber; everything else falls through to red so
  // unrecoverable crashes still read as "this is broken".
  const isAmber = code && (
    code === "embedding_dim_mismatch"
    || code === "embedding_migration_in_progress"
    || code === "llm_not_configured"
    || code === "integration_not_configured"
    || code === "tool_auth_expired"
    || code === "kb_empty"
    || code === "ingest_timeout"
  );
  const accent = isAmber ? (COLORS.orange || "#f59e0b") : COLORS.pink;
  const icon = isAmber ? "\u26A0" : "\u26D4";  // ⚠ vs ⛔

  // Friendly title above the body — two parts so the eye scans
  // "what kind of error" → "what to do" without a wall of text.
  const title = (() => {
    if (!isStructured) return null;
    switch (code) {
      case "embedding_dim_mismatch":      return "Re-embed required";
      case "embedding_migration_in_progress": return "Migration in progress";
      case "llm_not_configured":          return "LLM not configured";
      case "integration_not_configured":  return "Integration not configured";
      case "llm_rate_limit":              return "Rate limited";
      case "llm_timeout":                 return "Request timed out";
      case "kb_empty":                    return "Knowledge base empty";
      case "tool_auth_expired":           return "Tool credentials expired";
      default:                            return stage ? `${stage} stage failed` : "Ingestion failed";
    }
  })();

  return (
    <div style={{
      marginTop: 4,
      padding: "10px 12px",
      borderRadius: RADIUS.deep,
      background: `${accent}10`,
      border: `1px solid ${accent}38`,
      color: COLORS.text,
      fontSize: 11, lineHeight: 1.5,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
        <span style={{ color: accent, fontSize: 14, lineHeight: 1, paddingTop: 1 }}>
          {icon}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <div style={{ fontWeight: 700, color: accent, marginBottom: 3, letterSpacing: "0.01em" }}>
              {title}
            </div>
          )}
          <div style={{
            color: COLORS.text,
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
          }}>
            {message}
          </div>
          {(deepLink || technical) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
              {deepLink && (
                <a
                  href={deepLink}
                  style={{
                    fontSize: 10, fontWeight: 600,
                    padding: "4px 10px", borderRadius: 6,
                    background: accent, color: "#fff",
                    textDecoration: "none",
                    letterSpacing: "0.02em",
                  }}
                >
                  Resolve &rarr;
                </a>
              )}
              {technical && (
                <button
                  type="button"
                  onClick={() => setShowTech(s => !s)}
                  style={{
                    fontSize: 10, fontWeight: 600,
                    padding: "4px 8px", borderRadius: 6,
                    background: "transparent",
                    color: COLORS.textMuted,
                    border: `1px solid ${COLORS.border}`,
                    cursor: "pointer",
                    letterSpacing: "0.02em",
                  }}
                >
                  {showTech ? "Hide" : "Show"} technical details
                </button>
              )}
            </div>
          )}
          {showTech && technical && (
            <div style={{
              marginTop: 8, padding: "6px 9px",
              borderRadius: 6,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 10, lineHeight: 1.5,
              wordBreak: "break-all", whiteSpace: "pre-wrap",
            }}>
              {technical}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Per-stage ingestion timeline (Track A.4 / design doc §18.1).
// Exported for unit testing.
export function StageTimeline({ stages, currentStage }) {
  const colorFor = (outcome, isCurrent) => {
    if (isCurrent && outcome === "pending") return "#3b82f6";
    switch (outcome) {
      case "ok": return "#10b981";
      case "skipped": return COLORS.border || "#6b7280";
      case "error": return "#f59e0b";
      case "pending": return "transparent";
      default: return COLORS.textMuted || "#9ca3af";
    }
  };
  const isCurrent = (stage) => currentStage === stage.stage && stage.outcome === "pending";

  return (
    <div style={{ marginTop: 8, display: "flex", alignItems: "stretch", gap: 4, fontSize: 9 }}>
      {stages.map((s) => {
        const fg = colorFor(s.outcome, isCurrent(s));
        const tooltipParts = [
          s.stage,
          s.outcome === "error" && s.error ? `error: ${String(s.error).slice(0, 200)}` : null,
          s.detail?.attempt ? `attempt ${s.detail.attempt}` : null,
          s.detail?.elapsed_ms ? `${s.detail.elapsed_ms}ms` : null,
        ].filter(Boolean);
        return (
          <div
            key={s.stage}
            title={tooltipParts.join(" · ")}
            style={{
              flex: 1, minWidth: 18,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
              opacity: s.outcome === "pending" && !isCurrent(s) ? 0.4 : 1,
            }}
          >
            <div style={{
              width: "100%", height: 6, borderRadius: 3,
              background: fg,
              border: s.outcome === "pending" ? `1px solid ${COLORS.border || "#374151"}` : "none",
            }} />
            <div style={{ color: COLORS.textMuted || "#9ca3af", textTransform: "capitalize", whiteSpace: "nowrap" }}>
              {s.stage.replace("_", " ")}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// Per-page progress bar (Batch 8.2 / §18.1).
// Exported for unit testing.
export function PageProgressBar({ pp }) {
  const total = pp.total || 0;
  const done = pp.done || 0;
  const failed = pp.failed || 0;
  const captioned = pp.captioned || 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const lastIdx = pp.last_page_index;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: COLORS.textMuted, marginBottom: 3 }}>
        <span>
          {"Pages: "}
          <span style={{ color: COLORS.text, fontWeight: 700 }}>{done}</span>
          {" / "}
          <span style={{ color: COLORS.text, fontWeight: 700 }}>{total}</span>
          {captioned > 0 && (
            <span style={{ marginLeft: 10 }}>
              {"Captioned: "}<span style={{ color: COLORS.cyan || "#06b6d4", fontWeight: 700 }}>{captioned}</span>
            </span>
          )}
          {failed > 0 && (
            <span style={{ marginLeft: 10, color: "#f59e0b", fontWeight: 700 }}>
              {failed} failed
            </span>
          )}
        </span>
        <span>
          {pct}%
          {lastIdx != null && total > 1 && (
            <span style={{ marginLeft: 8, color: COLORS.textDim || "#6b7280" }}>
              last p.{lastIdx}
            </span>
          )}
        </span>
      </div>
      <div style={{
        position: "relative",
        height: 6, borderRadius: 3,
        background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        overflow: "hidden",
      }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: failed > 0 ? "#f59e0b" : "#10b981",
          transition: "width 240ms ease",
        }} />
        {lastIdx != null && total > 1 && (
          <div style={{
            position: "absolute",
            top: 0,
            left: `${Math.min(100, (lastIdx / total) * 100)}%`,
            width: 2, height: "100%",
            background: COLORS.text, opacity: 0.6,
            animation: "i2-progress-indeterminate 1500ms linear infinite",
          }} />
        )}
      </div>
    </div>
  );
}


// Ingestion result transparency (Batch 9.1-9.2).
//
// Visibility-gated lazy-fetch: hits /summary the first time the row
// scrolls INTO VIEW. Earlier we tried two extremes — eager-on-mount
// (caused 504s by stampeding N concurrent calls when many terminal
// jobs rendered at once) and only-on-expand (broke the inline
// collapsed-row text preview, made the row look like an empty
// disclosure). IntersectionObserver gives the right balance: only
// visible rows fetch, the inline preview text shows immediately for
// what's on screen, and 200 off-screen jobs don't fire 200 calls.
function ResultSummary({ jobId, status }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [seen, setSeen] = useState(false);
  const containerRef = useRef(null);
  // Failed jobs are "terminal" but have no usable result_summary — the
  // helper would emit a misleading "filename — ingested." stub. The
  // failure is already shown to the user via the row's red error
  // banner above; don't render the Summary disclosure at all for them.
  const isTerminal = status === "completed" || status === "completed_with_errors";

  // Intersection observer: flip `seen` to true the first time this row
  // enters the viewport (or scrollable parent). Once seen, the fetch
  // effect below kicks in. We never reset `seen` — re-renders shouldn't
  // re-fire the fetch.
  useEffect(() => {
    if (!isTerminal || seen) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);  // SSR / very old browsers — fall back to fetch-all
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setSeen(true);
          obs.disconnect();
          return;
        }
      }
    }, { rootMargin: "100px" });
    obs.observe(node);
    return () => obs.disconnect();
  }, [isTerminal, seen]);

  // Fetch when row is visible (or expanded — covers the case where the
  // user expands a row that happened to never trigger the observer
  // because it was already on screen at mount and IntersectionObserver
  // hadn't fired yet).
  //
  // CRITICAL: this effect must NOT depend on `loading` because we call
  // setLoading inside. If we did, setLoading(true) would re-run the
  // effect, the OLD cleanup would fire `cancelled = true`, and the
  // in-flight fetch would silently no-op its setSummary() — leaving
  // the row stuck without a summary forever. Use a ref to guard
  // against double-fetch instead.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!jobId || !isTerminal) return;
    if (!seen && !expanded) return;
    if (summary || fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    setLoading(true);
    ingestionApi.getJobSummary(jobId).then(res => {
      if (cancelled) return;
      if (res?.success) setSummary(res.data);
    }).catch(() => {
      // Allow retry on next visibility/expand cycle.
      fetchedRef.current = false;
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [jobId, isTerminal, seen, expanded, summary]);

  if (!isTerminal) return null;
  const fields = summary?.fields || {};
  const text = summary?.text || "";
  const fieldEntries = Object.entries(fields).filter(([k]) => k !== "filename" && k !== "duration_sec");

  const LABEL_OVERRIDES = {
    duration_human: "Duration",
    doc_type: "Type",
    sub_types: "Sub-types",
    screenshots_captioned: "Screenshots",
    tables_identified: "Tables",
    chunks_contextualized: "Contextualized chunks",
    video_keyframes: "Keyframes",
    transcript_words: "Transcript words",
    enrichment_summary_chars: "Summary length",
  };
  const labelFor = (k) => LABEL_OVERRIDES[k] || k.replace(/_/g, " ");

  return (
    <div ref={containerRef} style={{ marginTop: 8 }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: "flex", gap: 6, alignItems: "center",
          fontSize: 11, color: COLORS.textMuted, cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 9, transform: expanded ? "rotate(90deg)" : "rotate(0)", transition: "transform 150ms", display: "inline-block" }}>
          {"\u25B6"}
        </span>
        <span style={{ fontWeight: 600, color: COLORS.text }}>Summary</span>
        {!expanded && (text || loading) && (
          <span style={{ fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
            {loading && !text ? "loading…" : text}
          </span>
        )}
      </div>
      {expanded && (
        <div style={{
          marginTop: 6, padding: "8px 10px", borderRadius: RADIUS.deep,
          background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 8, lineHeight: 1.5 }}>
            {loading ? "Loading summary…" : (text || "No summary available.")}
          </div>
          {fieldEntries.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {fieldEntries.map(([k, v]) => {
                const vDisplay = Array.isArray(v) ? v.join(", ") : String(v);
                return (
                  <div key={k} style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "3px 8px", borderRadius: 4,
                    background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    fontSize: 10.5,
                  }}>
                    <span style={{ color: COLORS.textDim, textTransform: "capitalize" }}>
                      {labelFor(k)}
                    </span>
                    <span style={{ color: COLORS.text, fontWeight: 600 }}>{vDisplay}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// Per-agent flows have moved banner — points users at the per-agent
// Knowledge tab on the Detail Viewer for upload / library / settings.
function GraphTemplateNudge() {
  // F10 (2026-05-08): inform operators that AGE graph extraction
  // quality is template-dependent. Default-template agents now get a
  // domain-agnostic prompt (see F9) that produces a reasonable graph;
  // domain-specific templates (network-telecom, healthcare, etc.)
  // produce significantly richer graphs. This static banner avoids
  // the per-agent state-tracking complexity of the original F10
  // design while still surfacing the dependency to the operator
  // browsing Pipeline Jobs.
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("graph_template_nudge_dismissed") === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  return (
    <div style={{
      margin: "12px 16px 0", padding: "10px 14px", borderRadius: 8,
      border: `1px solid ${COLORS.violet || COLORS.accent}55`,
      background: `${COLORS.violet || COLORS.accent}10`,
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 11, fontWeight: 700,
                      color: COLORS.violet || COLORS.accent,
                      letterSpacing: "0.04em", textTransform: "uppercase" }}>
        Tip — Graph extraction
      </span>
      <span style={{ fontSize: 12, color: COLORS.text, flex: 1,
                      minWidth: 240, lineHeight: 1.5 }}>
        Knowledge-graph quality scales with the agent's domain template.
        Default-template agents get a domain-agnostic graph; pick a
        specific template (Network/Telecom, Healthcare, Legal, …) on
        the agent's Knowledge tab → Settings for richer entity extraction.
      </span>
      <button onClick={() => {
        setDismissed(true);
        try { localStorage.setItem("graph_template_nudge_dismissed", "1"); } catch {}
      }} style={{
        background: "transparent", border: `1px solid ${COLORS.border}`,
        color: COLORS.textDim, padding: "5px 10px", borderRadius: 6,
        cursor: "pointer", fontSize: 11,
      }}>Dismiss</button>
    </div>
  );
}


function PerAgentMovedBanner() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("pipeline_jobs_banner_dismissed") === "1"; } catch { return false; }
  });
  if (dismissed) return null;
  return (
    <div style={{
      margin: "12px 16px 0", padding: "10px 14px", borderRadius: 8,
      border: `1px solid ${COLORS.cyan}55`, background: `${COLORS.cyan}10`,
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.cyan, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        Per-agent flows have moved
      </span>
      <span style={{ fontSize: 12, color: COLORS.text, flex: 1, minWidth: 240, lineHeight: 1.5 }}>
        Upload, library, processing config, template selection, and per-agent re-enrich
        now live on each agent's Knowledge tab in the Detail Viewer. This page surfaces
        only the cross-agent jobs queue.
      </span>
      <button onClick={() => navigate("/library")} style={{
        padding: "5px 12px", borderRadius: 6, border: "none",
        background: COLORS.cyan, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer",
      }}>Go to Agent Library →</button>
      <button onClick={() => {
        setDismissed(true);
        try { localStorage.setItem("pipeline_jobs_banner_dismissed", "1"); } catch {}
      }} style={{
        background: "transparent", border: `1px solid ${COLORS.border}`,
        color: COLORS.textDim, padding: "5px 10px", borderRadius: 6,
        cursor: "pointer", fontSize: 11,
      }}>Dismiss</button>
    </div>
  );
}


const IngestionView = () => {
  const [agents, setAgents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const bp = useBreakpoint();

  // Load agents (used to display agent names on jobs)
  useEffect(() => {
    (async () => {
      try {
        const res = await agentApi.list();
        if (res.success) setAgents(res.data || []);
      } catch {}
    })();
  }, []);

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await ingestionApi.listJobs();
      if (res.success) setJobs(res.data?.jobs || []);
    } catch {}
    setJobsLoading(false);
  };

  useEffect(() => { fetchJobs(); }, []);

  // Auto-refresh while any job is in flight
  useEffect(() => {
    const hasRunning = jobs.some(j =>
      j.status === "running" || j.status === "pending" || j.status === "claimed" || j.status === "queued"
    );
    if (!hasRunning) return;
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [jobs]);

  const handleDeleteJob = async (jobId) => {
    try {
      await ingestionApi.deleteJob(jobId);
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch {}
  };

  const handleClearAllJobs = async () => {
    if (!window.confirm("Clear all pipeline jobs? This cannot be undone.")) return;
    try {
      await ingestionApi.clearJobs();
      setJobs([]);
    } catch {}
  };

  const statusColors = {
    pending: COLORS.orange,
    queued: COLORS.orange,
    claimed: COLORS.cyan,
    running: COLORS.cyan,
    completed: COLORS.green,
    completed_with_errors: COLORS.orange,
    failed: COLORS.pink,
    cancelled: COLORS.textMuted,
  };

  return (
    <div className="panel-scroll" style={{ flex: 1, overflow: "auto", padding: bp.isMobile ? 16 : 32 }}>
      <PerAgentMovedBanner />
      <GraphTemplateNudge />
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, margin: 0 }}>Pipeline Jobs</h2>
          <p style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 4 }}>
            Cross-agent ingestion queue (current + historical). Per-agent flows live on each agent's Knowledge tab.
          </p>
        </div>

        <div style={{
          padding: "0 4px 10px", marginBottom: 10,
          display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8,
        }}>
          {jobs.length > 0 && (
            <Button variant="danger" size="sm" onClick={handleClearAllJobs}>Clear All</Button>
          )}
          <Button variant="secondary" size="sm" onClick={fetchJobs} loading={jobsLoading}>Refresh</Button>
        </div>

        {jobs.length === 0 ? (
          <div style={{
            padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 13,
            borderRadius: RADIUS.inner,
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            boxShadow: MATERIALS.spatialShadow(),
          }}>
            No ingestion jobs yet. Start a pipeline from an agent's Knowledge tab.
          </div>
        ) : (
          <div style={{
            display: "flex", flexDirection: "column", gap: 10,
            maxHeight: 720, overflowY: "auto",
            paddingRight: 4,
          }} className="panel-scroll">
            {jobs.map((job, idx) => {
                const agentName = agents.find(a => a.id === job.agent_id)?.name || job.agent_id?.slice(0, 8);
                const filesExpanded = expandedJob === job.id;
                const progress = job.total_documents > 0
                  ? Math.round((job.processed_documents / job.total_documents) * 100) : 0;
                const isActive = job.status === "running" || job.status === "pending"
                  || job.status === "queued" || job.status === "claimed";

                let duration = null;
                if (job.started_at && job.completed_at) {
                  const secs = Math.round((new Date(job.completed_at) - new Date(job.started_at)) / 1000);
                  duration = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
                }

                const sourceIcon = { crawl: "\uD83C\uDF10", upload: "\uD83D\uDCC4", bulk_upload: "\uD83D\uDDDC\uFE0F", text: "\u270F\uFE0F" }[job.source_type] || "\uD83D\uDCE6";
                const filenames = job.source_config?.filenames || [];
                const FILE_PREVIEW = 3;
                const hasMoreFiles = filenames.length > FILE_PREVIEW;

                const Chip = ({ label, value, accent }) => (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "3px 8px", borderRadius: 5,
                    background: COLORS.surface, border: `1px solid ${COLORS.border}`, fontSize: 10.5,
                  }}>
                    <span style={{ color: COLORS.textDim }}>{label}</span>
                    <span style={{ fontWeight: 600, color: accent || COLORS.text }}>{value}</span>
                  </div>
                );

                return (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      type: "spring", stiffness: 400, damping: 25,
                      delay: Math.min(idx * 0.05, 0.4),
                    }}
                    whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 25 } }}
                    style={{
                      padding: 16,
                      borderRadius: RADIUS.inner,
                      background: COLORS.surface,
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: MATERIALS.spatialShadow(),
                    }}
                  >

                    {/* Row 1 — status / type / agent / timestamp / delete */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 7 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                        <Badge color={statusColors[job.status] || COLORS.textDim}
                          style={{ letterSpacing: "0.05em", textTransform: "uppercase", fontSize: 9.5 }}>
                          {job.status.replace(/_/g, " ")}
                        </Badge>
                        <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.text }}>
                          {sourceIcon} {job.source_type.replace(/_/g, " ")}
                        </span>
                        <span style={{ fontSize: 11, color: COLORS.textDim }}>&rarr;</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.accentText }}>{agentName}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 10, color: COLORS.textDim, whiteSpace: "nowrap" }}>
                          {job.created_at
                            ? new Date(job.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                            : "\u2014"}
                        </span>
                        <button onClick={() => handleDeleteJob(job.id)} title="Delete job" style={{
                          width: 20, height: 20, borderRadius: 4, border: `1px solid ${COLORS.border}`,
                          background: "transparent", color: COLORS.textDim, fontSize: 13,
                          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                          padding: 0, lineHeight: 1,
                        }}>&times;</button>
                      </div>
                    </div>

                    {/* Row 2 — source details */}
                    <div style={{ marginBottom: 9 }}>
                      {job.source_type === "text" && job.source_config?.source && (
                        <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                          <span style={{ fontWeight: 500, color: COLORS.text }}>{job.source_config.source}</span>
                          {job.source_config.text_length != null && (
                            <span style={{ marginLeft: 6, color: COLORS.textDim }}>
                              {job.source_config.text_length.toLocaleString()} chars
                            </span>
                          )}
                        </span>
                      )}
                      {job.source_type === "crawl" && job.source_config?.url && (
                        <span style={{ fontSize: 11, color: COLORS.text, wordBreak: "break-all" }}>
                          {job.source_config.url}
                        </span>
                      )}
                      {job.source_type === "bulk_upload" && (
                        <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                          <span style={{ fontWeight: 500, color: COLORS.text }}>
                            {job.source_config?.filename}
                          </span>
                          {job.source_config?.file_count != null && (
                            <span style={{ marginLeft: 6, color: COLORS.textDim }}>
                              {job.source_config.file_count} file{job.source_config.file_count !== 1 ? "s" : ""} in archive
                            </span>
                          )}
                        </span>
                      )}
                      {job.source_type === "upload" && filenames.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 5px" }}>
                          {(filesExpanded ? filenames : filenames.slice(0, FILE_PREVIEW)).map((f, i) => (
                            <span key={i} style={{
                              padding: "2px 7px", borderRadius: 4,
                              background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                              color: COLORS.textMuted, fontFamily: "monospace", fontSize: 10.5,
                            }}>{f}</span>
                          ))}
                          {hasMoreFiles && (
                            <button onClick={() => setExpandedJob(filesExpanded ? null : job.id)} style={{
                              padding: "2px 8px", borderRadius: 4, background: "transparent",
                              border: `1px solid ${COLORS.border}`, color: COLORS.accentText,
                              fontSize: 10.5, cursor: "pointer",
                            }}>
                              {filesExpanded ? "\u25B2 less" : `+${filenames.length - FILE_PREVIEW} more`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Row 3 — stat chips */}
                    <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginBottom: (job.error_message || isActive) ? 9 : 0 }}>
                      <Chip label="Docs"
                        value={`${job.processed_documents ?? 0} / ${job.total_documents ?? 0}`}
                        accent={job.processed_documents > 0 && job.processed_documents === job.total_documents ? COLORS.cyan : undefined} />
                      <Chip label="Chunks" value={job.total_chunks ?? 0} />
                      {duration && <Chip label="Duration" value={duration} />}
                      {job.processing_config?.chunk_size != null && (
                        <Chip label="Chunk size" value={job.processing_config.chunk_size} />
                      )}
                      {job.processing_config?.chunk_overlap != null && (
                        <Chip label="Overlap" value={job.processing_config.chunk_overlap} />
                      )}
                    </div>

                    {/* Progress bar */}
                    {isActive && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 10, color: COLORS.textDim }}>Processing&hellip;</span>
                          <span style={{ fontSize: 10, color: COLORS.textDim }}>{progress}%</span>
                        </div>
                        <div style={{ height: 4, borderRadius: 3, background: COLORS.surface, overflow: "hidden" }}>
                          <div style={{
                            width: `${progress}%`, height: "100%", borderRadius: 3,
                            background: `linear-gradient(90deg, ${COLORS.accent}, ${COLORS.cyan})`,
                            transition: "width 0.4s",
                          }} />
                        </div>
                      </div>
                    )}

                    {/* Per-stage timeline + per-page progress for v2 jobs */}
                    {Array.isArray(job?.stages) && job.stages.length > 0 && (
                      <StageTimeline stages={job.stages} currentStage={job?.stage} />
                    )}
                    {job?.page_progress && job.page_progress.total > 0 && (
                      <PageProgressBar pp={job.page_progress} />
                    )}
                    <ResultSummary jobId={job.id} status={job.status} />

                    {/* Error */}
                    {job.error_message && (
                      <ErrorBanner raw={job.error_message} />
                    )}
                  </motion.div>
                );
              })}
          </div>
        )}

        <style>{`
          @keyframes i2-progress-indeterminate {
            0%   { background-position: -50% 0; }
            100% { background-position: 150% 0; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default IngestionView;
